import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'
import apiService from '../services/api.service'
import Loader from '../components/ui/loader.jsx'
import Button from '../components/ui/button-old'
import Validator from '../utils/validator'

const VerifyEmailToken = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { t, languageId } = useLanguage()

  const [verificationState, setVerificationState] = useState('verifying') // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('')
  const [isResending, setIsResending] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const [isLocked, setIsLocked] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [countdown, setCountdown] = useState(5)
  const hasVerified = useRef(false)
  const lockoutTimerRef = useRef(null)

  // Email fallback state — shown when token is no longer in DB
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState('')

  const handleRedirectToSignIn = useCallback(() => {
    const tenantUrlName = tenant?.tenanturlname || ''
    navigate(`/${tenantUrlName}/signin`)
  }, [tenant?.tenanturlname, navigate])

  // --- Rate limit response handler (shared by both token and email resend) ---
  const handleRateLimitError = useCallback(
    (error) => {
      const reason = error.data?.reason

      if (reason === 'RESEND_ACCOUNT_LOCKED') {
        setErrorMessage(
          t('verifyEmailToken.accountLocked') ||
            'Your account is temporarily locked. Please try again later.',
        )
        setIsLocked(true)
        setResendDisabled(true)

        const lockRetryAfter = error.data?.retry_after
        if (lockRetryAfter) {
          if (lockoutTimerRef.current) {
            clearTimeout(lockoutTimerRef.current)
          }
          lockoutTimerRef.current = setTimeout(
            () => {
              setIsLocked(false)
              setResendDisabled(false)
              setErrorMessage('')
            },
            Number(lockRetryAfter) * 1000,
          )
        }
      } else {
        const retryAfter = error.data?.retry_after || 60
        setErrorMessage(
          t('verifyEmailToken.resendRateLimited', {
            seconds: Number(retryAfter),
          }) ||
            `You've requested verification too many times. Please try again later.`,
        )
        setResendDisabled(true)
        setResendCountdown(Number(retryAfter))
      }
    },
    [t],
  )

  // --- Handle successful resend response (shared) ---
  const handleResendSuccess = useCallback((response) => {
    setResendSuccess(true)
    setErrorMessage('')
    setEmailError('')
    const retryAfter = response?.retry_after || 60
    setResendDisabled(true)
    setResendCountdown(Number(retryAfter))
  }, [])

  const verifyEmail = useCallback(async () => {
    try {
      const encodedToken = encodeURIComponent(token)
      await apiService.post('/api/kc/verify-email', {
        verificationToken: encodedToken,
        clienttenantid: tenant.tenantid,
      })
      setVerificationState('success')
    } catch (error) {
      console.error('Verification error:', error)
      setVerificationState('error')

      if (error.status === 400) {
        const reason = error.data?.reason

        if (reason === 'EXPIRED_VERIFICATION_TOKEN') {
          setErrorMessage(
            t('verifyEmailToken.expiredToken') ||
              'Your verification link has expired. Please request a new one.',
          )
          return
        }

        if (reason === 'INVALID_VERIFICATION_TOKEN') {
          setErrorMessage(
            t('verifyEmailToken.invalidToken') ||
              'Invalid or expired verification token.',
          )
          return
        }

        setErrorMessage(
          t('verifyEmailToken.invalidToken') ||
            'Invalid or expired verification token.',
        )
      } else if (error.status === 404) {
        setErrorMessage(
          t('verifyEmailToken.tokenNotFound') ||
            'Verification token not found. Please try signing up again.',
        )
      } else if (error.status === 0) {
        setErrorMessage(
          t('verifyEmailToken.networkError') ||
            'Network error. Please check your connection and try again.',
        )
      } else {
        setErrorMessage(
          t('verifyEmailToken.verificationFailed') ||
            'Verification failed. Please try again or contact support.',
        )
      }
    }
  }, [token, tenant?.tenantid, t])

  useEffect(() => {
    if (token && tenant?.tenantid && !hasVerified.current) {
      hasVerified.current = true
      verifyEmail()
    } else if (!token) {
      setVerificationState('error')
      setErrorMessage('No verification token provided.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenant?.tenantid])

  useEffect(() => {
    return () => {
      if (lockoutTimerRef.current) {
        clearTimeout(lockoutTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (verificationState === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else if (verificationState === 'success' && countdown === 0) {
      handleRedirectToSignIn()
    }
  }, [verificationState, countdown, handleRedirectToSignIn])

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(
        () => setResendCountdown(resendCountdown - 1),
        1000,
      )
      return () => clearTimeout(timer)
    } else if (resendCountdown === 0 && resendDisabled && !isLocked) {
      setResendDisabled(false)
    }
  }, [resendCountdown, resendDisabled, isLocked])

  // --- Token-based resend (first Try Again) ---
  const handleRetry = () => {
    const resend = async () => {
      try {
        setIsResending(true)
        setErrorMessage('')
        setResendSuccess(false)

        const encodedToken = encodeURIComponent(token)
        const response = await apiService.post('/api/kc/resend-verification', {
          verificationToken: encodedToken,
          clienttenantid: tenant.tenantid,
        })

        handleResendSuccess(response)
      } catch (error) {
        if (error.status === 429 || error.isRateLimit) {
          handleRateLimitError(error)
        } else if (
          error.status === 400 &&
          error.data?.reason === 'TOKEN_NOT_FOUND'
        ) {
          // Token no longer exists in DB (replaced by previous resend).
          // Switch to email form so the user can identify themselves.
          setShowEmailForm(true)
          setErrorMessage(
            t('verifyEmailToken.tokenExpiredEnterEmail') ||
              'This verification link is no longer valid. Please enter your email to request a new one.',
          )
        } else {
          setErrorMessage(
            t('verifyEmailToken.resendFailed') ||
              'Unable to resend verification email. Please try again later.',
          )
        }
      } finally {
        setIsResending(false)
      }
    }
    resend()
  }

  // --- Email-based resend (after token is stale) ---
  const handleEmailResend = async (e) => {
    e.preventDefault()

    const trimmedEmail = emailInput.trim()

    // Validate email
    if (!trimmedEmail) {
      setEmailError(
        t('verifyEmailToken.emailRequired') || 'Email address is required.',
      )
      return
    }
    if (!Validator.isValidEmail(trimmedEmail)) {
      setEmailError(
        t('verifyEmailToken.emailInvalid') ||
          'Please enter a valid email address.',
      )
      return
    }

    setEmailError('')

    try {
      setIsResending(true)
      setErrorMessage('')
      setResendSuccess(false)

      const response = await apiService.post(
        '/api/kc/resend-verification-email',
        {
          clientemail: trimmedEmail,
          clienttenantid: tenant?.tenantid || 1011,
          defaultlanguageid: languageId || 490,
        },
      )

      handleResendSuccess(response)
    } catch (error) {
      if (error.status === 429 || error.isRateLimit) {
        handleRateLimitError(error)
      } else {
        setErrorMessage(
          t('verifyEmailToken.resendFailed') ||
            'Unable to resend verification email. Please try again later.',
        )
      }
    } finally {
      setIsResending(false)
    }
  }

  // --- Shared banner renderer ---
  const renderBanner = () => (
    <div className="main-banner w-full max-w-[648px] mx-auto">
      <div className="w-full aspect-[648/381] overflow-hidden">
        <img
          src={
            tenant?.tenantcontentconfig?.images?.verify_email_token
              ? `${process.env.REACT_APP_CLOUDFRONT}tenantbannerimages/${tenant?.tenanturlname}/mob_${tenant.tenantcontentconfig.images.verify_email_token}`
              : require('../assets/images/backgrounds/main-banner.png')
          }
          alt={t('common.bannerAlt')}
          className="block md:hidden w-full h-full object-contain border border-gray-300 rounded-md"
          loading="lazy"
          onError={(e) => {
            e.target.src = require('../assets/images/backgrounds/main-banner.png')
          }}
        />
        <img
          src={
            tenant?.tenantcontentconfig?.images?.verify_email_token
              ? `${process.env.REACT_APP_CLOUDFRONT}tenantbannerimages/${tenant?.tenanturlname}/web_${tenant.tenantcontentconfig.images.verify_email_token}`
              : require('../assets/images/backgrounds/main-banner.png')
          }
          alt={t('common.bannerAlt')}
          className="hidden md:block w-full h-full object-contain border border-gray-300 rounded-md"
          loading="lazy"
          onError={(e) => {
            e.target.src = require('../assets/images/backgrounds/main-banner.png')
          }}
        />
      </div>
    </div>
  )

  // Show loading state while verifying
  if (verificationState === 'verifying') {
    return (
      <Loader
        fullscreen
        label={t('verifyEmailToken.verifying') || 'Verifying your email...'}
        size="xl"
      />
    )
  }

  // Show success state
  if (verificationState === 'success') {
    return (
      <div className="verify-email-token-page">
        {renderBanner()}

        <div className="flex flex-col items-center max-w-md mx-auto mt-8 px-4 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <FaCheckCircle className="text-green-600 text-3xl" />
          </div>

          <h1 className="text-2xl font-extrabold text-theme-secondary mb-4">
            {t('verifyEmailToken.successTitle') || 'Email Verified!'}
          </h1>

          <p className="text-sm text-theme-secondary mb-6">
            {t('verifyEmailToken.successDescription') ||
              'Your email has been successfully verified. You can now sign in to your account.'}
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 w-full">
            <p className="text-sm text-green-800">
              {t('verifyEmailToken.redirecting') ||
                'Redirecting to sign in page in'}{' '}
              <strong>{countdown}</strong>{' '}
              {countdown !== 1
                ? t('common.seconds') || 'seconds'
                : t('common.second') || 'second'}
              ...
            </p>
          </div>

          <Button
            id="go-to-signin-btn"
            variant="primary"
            type="button"
            onClick={handleRedirectToSignIn}
            className="w-full"
          >
            {t('verifyEmailToken.goToSignIn') || 'Go to Sign In Now'}
          </Button>
        </div>
      </div>
    )
  }

  // Show error state
  if (verificationState === 'error') {
    return (
      <div className="verify-email-token-page">
        {renderBanner()}

        {isResending && (
          <Loader
            fullscreen
            label={
              t('verifyEmailToken.resending') ||
              'Resending verification email...'
            }
            size="xl"
          />
        )}

        <div className="flex flex-col items-center max-w-md mx-auto mt-8 px-4 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <FaTimesCircle className="text-red-600 text-3xl" />
          </div>

          <h1 className="text-2xl font-extrabold text-theme-secondary mb-4">
            {t('verifyEmailToken.errorTitle') || 'Verification Failed'}
          </h1>

          {/* Resend success message */}
          {resendSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md w-full">
              <p className="text-sm text-green-600">
                {t('verifyEmailToken.resendSuccess') ||
                  'A new verification email has been sent. Please check your inbox.'}
              </p>
            </div>
          )}

          {/* Error / rate limit message */}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md w-full">
              <p className="text-sm text-red-600">{errorMessage}</p>
            </div>
          )}

          {!resendSuccess && !errorMessage && (
            <p className="text-sm text-theme-secondary mb-6">
              {t('verifyEmailToken.unexpectedError') ||
                'An unexpected error occurred. Please try again.'}
            </p>
          )}

          {/* Email form — shown when token is no longer in DB */}
          {showEmailForm && (
            <form
              className="w-full mb-4"
              onSubmit={handleEmailResend}
              noValidate
            >
              <div className="grid gap-1 text-left">
                <label
                  htmlFor="resend-email"
                  className="text-sm font-normal text-theme-accent"
                >
                  {t('verifyEmailToken.emailLabel') || 'Email address'}
                </label>
                <input
                  id="resend-email"
                  type="email"
                  name="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value)
                    if (emailError) setEmailError('')
                  }}
                  required
                  className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                    emailError ? 'border-red-500' : 'border-gray-300'
                  } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                  placeholder={
                    t('verifyEmailToken.emailPlaceholder') ||
                    'Enter your email address'
                  }
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={emailError ? 'true' : 'false'}
                  aria-describedby={
                    emailError ? 'resend-email-error' : undefined
                  }
                  disabled={isResending || isLocked}
                />
                {emailError && (
                  <div
                    id="resend-email-error"
                    className="text-xs text-red-500 mt-1 flex items-center gap-2"
                  >
                    <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                    {emailError}
                  </div>
                )}
              </div>

              <Button
                id="resend-email-btn"
                variant="primary"
                type="submit"
                disabled={resendDisabled || isResending || isLocked}
                className="w-full mt-3"
              >
                {isResending
                  ? t('verifyEmailToken.resending') || 'Resending...'
                  : isLocked
                    ? t('verifyEmailToken.sendVerification') ||
                      'Send Verification Email'
                    : resendDisabled && resendCountdown > 0
                      ? `${t('verifyEmailToken.sendVerification') || 'Send Verification Email'} (${resendCountdown}s)`
                      : t('verifyEmailToken.sendVerification') ||
                        'Send Verification Email'}
              </Button>
            </form>
          )}

          {/* Token-based Try Again button — shown only when token is still usable */}
          {!showEmailForm && (
            <div className="space-y-3 w-full">
              <Button
                id="retry-btn"
                variant="primary"
                type="button"
                onClick={handleRetry}
                disabled={resendDisabled || isResending || isLocked}
                className="w-full"
              >
                {isResending
                  ? t('verifyEmailToken.resending') || 'Resending...'
                  : isLocked
                    ? t('verifyEmailToken.tryAgain') || 'Try Again'
                    : resendDisabled && resendCountdown > 0
                      ? `${t('verifyEmailToken.tryAgain') || 'Try Again'} (${resendCountdown}s)`
                      : t('verifyEmailToken.tryAgain') || 'Try Again'}
              </Button>
            </div>
          )}

          <div className="w-full mt-3">
            <Button
              id="go-to-signin-error-btn"
              variant="secondary"
              type="button"
              onClick={handleRedirectToSignIn}
              className="w-full"
            >
              {t('verifyEmailToken.goToSignIn') || 'Go to Sign In'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default VerifyEmailToken
